import { computed, shallowRef, watch, type ComputedRef, type ShallowRef } from 'vue'

/*
 * Images servies par l'API derrière l'authentification (photo de profil, logo de compagnie), chargées une fois par
 * (id, version) pour toute l'app : la même image affichée dans la sidebar et dans dix listes ne fait qu'une requête.
 * Le cache vit au niveau du module qui le crée (partagé entre composants) ; aucune URL n'est révoquée au démontage,
 * puisqu'un autre composant peut l'afficher.
 * Une version lue dans une liste chargée plus tôt peut coexister avec la nouvelle : chacune garde son entrée,
 * sinon les deux affichages se la disputeraient. oublier() libère les URL d'un id dont l'image change.
 */
interface Entree {
  url: ShallowRef<string | null>
}

export interface CacheImages {
  /** URL de l'image, `undefined` sans image, pendant le chargement ou en cas d'échec (on affiche un repli). */
  use: (source: () => { id?: string; version?: string }) => ComputedRef<string | undefined>
  /** Révoque les URL de toutes les versions de l'id (après l'envoi ou le retrait de son image). */
  oublier: (id: string) => void
}

export function creerCacheImages(charger: (id: string, version: string) => Promise<Blob>): CacheImages {
  const cache = new Map<string, Entree>()
  const cle = (id: string, version: string): string => `${id}:${version}`

  function entree(id: string, version: string): Entree {
    const existante = cache.get(cle(id, version))
    if (existante) return existante
    const nouvelle: Entree = { url: shallowRef(null) }
    cache.set(cle(id, version), nouvelle)
    charger(id, version)
      .then((blob) => {
        // Oubliée entre-temps : l'URL ne serait jamais révoquée
        if (cache.get(cle(id, version)) === nouvelle) nouvelle.url.value = URL.createObjectURL(blob)
      })
      // Échec gardé en cache : repli jusqu'à la version suivante, sans nouvel essai à chaque affichage
      .catch(() => undefined)
    return nouvelle
  }

  function oublier(id: string): void {
    for (const [c, e] of cache) {
      if (!c.startsWith(`${id}:`)) continue
      if (e.url.value) URL.revokeObjectURL(e.url.value)
      cache.delete(c)
    }
  }

  function use(source: () => { id?: string; version?: string }): ComputedRef<string | undefined> {
    const courante = shallowRef<Entree | null>(null)
    watch(
      () => [source().id, source().version] as const,
      ([id, version]) => {
        courante.value = id && version ? entree(id, version) : null
      },
      { immediate: true }
    )
    return computed(() => courante.value?.url.value ?? undefined)
  }

  return { use, oublier }
}
